import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "../landing/Reveal";
import { Phone3D } from "../landing/Phone3D";
import type { UseCaseConfig } from "../../use-cases/types";

interface NarrativeShowcaseProps {
  narrativeSample: NonNullable<UseCaseConfig["narrativeSample"]>;
}

/**
 * Phone preview of a sample Year in Review. The artifact (a Year in Review
 * for a fictional account/school) sits inside the same 3D tilt frame used on
 * the marketing home page hero. Fictionality is disclosed in the caption
 * beside the phone, not in a page-wide banner.
 */
export const NarrativeShowcase = ({
  narrativeSample,
}: NarrativeShowcaseProps) => {
  const reducedMotion = useReducedMotion();

  const demoUrl = import.meta.env.DEV
    ? `/?report=${narrativeSample.reportId}`
    : `${window.location.origin}/?report=${narrativeSample.reportId}`;
  const demoEmbedUrl = `${demoUrl}&format=video`;

  return (
    <section className="bg-fwd-soft pt-12 md:pt-16 pb-24 md:pb-32 px-6 md:px-16 lg:px-24 relative overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16">
          <Reveal>
            <div className="relative flex justify-center">
              {narrativeSample.watermark && (
                <div
                  aria-hidden="true"
                  className="absolute -top-8 -left-12 font-display text-[14rem] font-normal text-fwd-ink/5 leading-none pointer-events-none select-none tracking-tighter hidden md:block"
                >
                  {narrativeSample.watermark}
                </div>
              )}
              <motion.div
                animate={reducedMotion ? undefined : { y: [0, -8, 0] }}
                transition={
                  reducedMotion
                    ? undefined
                    : {
                        duration: 5,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1.2,
                      }
                }
                className="relative z-10"
              >
                <Phone3D href={demoUrl} ctaLabel={narrativeSample.ctaLabel}>
                  <div className="w-[320px] md:w-[360px] bg-fwd-ink rounded-[44px] p-4 shadow-[0_30px_80px_rgba(17,17,24,0.22),0_0_0_1px_rgba(17,17,24,0.08)]">
                    <div className="w-16 h-1.5 bg-white/5 rounded-full mx-auto mb-3" />
                    <div className="rounded-[30px] overflow-hidden h-[600px] bg-[#0a0a12] relative">
                      <iframe
                        src={demoEmbedUrl}
                        title={narrativeSample.sampleTitle}
                        className="w-full h-full border-none block pointer-events-none"
                        loading="lazy"
                        tabIndex={-1}
                      />
                      <div className="absolute inset-0 z-10" />
                    </div>
                  </div>
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-fwd-red text-white text-[0.65rem] font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full whitespace-nowrap z-30">
                    {narrativeSample.badgeLabel}
                  </div>
                </Phone3D>
              </motion.div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="max-w-md text-center lg:text-left">
              <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase block mb-4">
                {narrativeSample.eyebrow}
              </span>
              <h2 className="font-display text-[clamp(1.7rem,2.6vw,2.4rem)] font-normal leading-tight text-fwd-ink mb-5">
                {narrativeSample.captionHeadline}
              </h2>
              <p className="font-serif text-fwd-ink/75 text-lg leading-relaxed mb-6">
                {narrativeSample.captionBody}
              </p>
              <p className="text-fwd-mid text-sm leading-relaxed border-l-2 border-fwd-yellow pl-4 mb-6 text-left">
                {narrativeSample.disclosure}
              </p>
              <a
                href={demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-fwd-ink text-sm font-medium border-b border-fwd-ink/30 pb-0.5 hover:text-fwd-red hover:border-fwd-red transition-colors"
              >
                {narrativeSample.fullScreenLabel}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
